










































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import MobileDetect from 'mobile-detect';
import { FormState } from '@/components/prine/enums/FormState';
import ConfirmModal from '@/components/prine/ConfirmModal.vue';

import type { Project } from '@/models/project';
import type { User } from '@/models/user';
import type { XLesson } from '@/models/xlesson';
import type { XSubProcess } from '@/models/xsubprocess';
import type { XProcess } from '@/models/xprocess';
import type { Tag } from '@/models/tag';
import { ApprovalState } from '@/components/prine/enums/ApprovalState';

import { xSubProcessWebservice } from '@/webservices/xsubprocess';
import { xLessonWebservice } from '@/webservices/xlesson';
import { tagWebservice } from '@/webservices/tag';
import type { CompanySetting } from '@/models/companySetting';
import WysiwygEditor from '@/components/prine/WysiwygEditor.vue';
import { projectWebservice } from '@/webservices/project';
import PrineButton from '@/components/prine/PrineButton.vue';
import { xProcessWebservice } from '@/webservices/xprocess';
import PreviewLessons from '@/components/knowledge/lessons/PreviewLessons.vue';
import { hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';

@Component({
    name: 'StoreUpdateXLessonModal',
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    methods: { hasPermission },
    components: {
        ConfirmModal,
        WysiwygEditor,
        PrineButton,
        PreviewLessons,
    },
})
export default class StoreUpdateXLessonModal extends Vue {
    get lessonsDisabled() {
        return this.subProcess === null;
    }

    private get isDesktop() {
        const isNotDesktop = window.matchMedia('only screen and (max-width: 1300px)').matches;
        const md = new MobileDetect(window.navigator.userAgent);
        return !(md.mobile() || md.phone() || md.tablet() || isNotDesktop);
    }

    public FormState = FormState;

    // Data
    private disabled: boolean = false;
    private errorMsg: string = '';
    private isConfirmModalActive: boolean = false;
    private showMandatoryOption: boolean = false;
    private state: FormState = FormState.New;

    private lesson: XLesson = {} as XLesson;
    private tags: Tag[] = new Array<Tag>();
    private selectedTags: Tag[] = new Array<Tag>();
    private projects: Project[] = new Array<Project>();
    private parentLesson: XLesson | null = null;
    private lessons = new Array<XLesson>();
    private checkedParentLesson: XLesson[] = new Array<XLesson>();

    private loading = {
        data: false,
        color: '#fdc05d',
        size: '40px',
    };

    private xProcesses = new Array<XProcess>();
    private xProcess: XProcess = {} as XProcess;
    private subProcesses = new Array<XSubProcess>();
    private subProcess: XSubProcess | null = null;
    private selectedProjectDropdown: Project = {} as Project;

    @Getter('account/userInfo') private userInfo: User;
    @Getter('project/selectedProject') private selectedProject: Project;
    @Getter('account/companySetting') private companySetting: CompanySetting;
    @Getter('utils/currentLocale') private currentLocale: string;

    @Prop() private selectedPhase: XProcess;
    @Prop() private selectedLesson: XLesson;

    private xEditorImagePaths: string[] = [];

    @Watch('xProcess')
    private xProcessChanged(newXProcess, oldXProcess) {
        this.loadSubProcesses();

        if (oldXProcess === newXProcess || (this.subProcess && this.subProcess.x_process.id === newXProcess.id)) {
            return;
        }
        this.subProcess = null;
        this.parentLesson = null;
    }

    @Watch('subProcess')
    private onSubProcessChanged(newSubProcess, oldSubProcess) {
        if (oldSubProcess === newSubProcess || !newSubProcess) {
            return;
        }

        this.loadLessons(newSubProcess.id);
        this.parentLesson = null;
    }

    private selectedParentLesson(value: XLesson[]) {
        this.checkedParentLesson = value;
        if (value.length !== 0) {
            this.parentLesson = value[0];
        } else {
            this.parentLesson = null;
        }
    }

    // Mounted is called initially
    private mounted() {
        this.loading.data = true;

        const promises = [this.loadTags(), this.loadXProcesses(), this.loadProjects()];

        if (this.selectedLesson && this.selectedLesson.id) {
            promises.push(this.loadLesson(this.selectedLesson.id));
            this.state = FormState.Update;
        }
        // If the user is content admin or master show mandatory option
        this.showMandatoryOption = hasPermission(PERMISSIONS.APPEX_X_LESSON_MANDATORY);

        Promise.all(promises).then(() => {
            this.loading.data = false;
        });
    }

    private async loadLessons(id: number): Promise<void> {
        try {
            const response = await xLessonWebservice.load(id);
            this.lessons = response.data;

            if (this.lesson.parent) {
                this.parentLesson = this.lesson.parent;
                this.checkedParentLesson = [];
                this.checkedParentLesson.push(this.parentLesson);
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async loadXProcesses(): Promise<void> {
        try {
            const response = await xProcessWebservice.load();
            this.xProcesses = response.data;

            if (this.selectedPhase) {
                this.xProcess = this.selectedPhase;
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async loadSubProcesses(): Promise<void> {
        try {
            const response = await xSubProcessWebservice.load(this.xProcess.id, ApprovalState.ALL);
            this.subProcesses = response.data;
        } catch (error) {
            console.error(error);
        }
    }

    private async loadProjects(): Promise<void> {
        try {
            const response = await projectWebservice.loadAll();
            this.projects = response.data;
        } catch (error) {
            console.error(error);
        }
    }

    private async loadTags(): Promise<void> {
        try {
            const response = await tagWebservice.load();
            this.tags = response.data;

            if (!this.selectedLesson) {
                this.selectedTags = this.tags.filter((tag) => tag.default);
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async loadLesson(lessonId: number): Promise<void> {
        try {
            const response = await xLessonWebservice.getById(lessonId);
            this.lesson = response.data;
            this.selectedTags = response.data.tags;

            if (this.lesson.project) {
                this.selectedProjectDropdown = this.lesson.project;
            }

            if (this.lesson.x_sub_process) {
                this.subProcess = this.lesson.x_sub_process;
                this.xProcess = this.subProcess.x_process;
            }
        } catch (error) {
            console.error(error);
        }
    }

    private save() {
        this.disabled = true;
        if (!this.validate()) {
            this.disabled = false;
            return 0;
        }
        if (this.subProcess !== null) {
            if (this.state === FormState.New) {
                // tslint:disable-next-line:max-line-length
                xLessonWebservice
                    .store(
                        this.lesson,
                        this.subProcess.id,
                        this.lesson.mandatory,
                        this.selectedTags,
                        null,
                        this.selectedProjectDropdown ? this.selectedProjectDropdown.id : 0,
                        this.parentLesson
                    )
                    .then(() => {
                        this.$buefy.toast.open({
                            duration: 3000,
                            message: this.$t('toastMessages.store.lesson').toString(),
                            type: 'is-success',
                        });
                        this.toggleModal(this.subProcess);
                        this.$emit('syncKnowledge');
                    })
                    .catch((error) => {
                        this.disabled = false;
                        console.log(error);
                    });
            } else if (this.state === FormState.Update) {
                // tslint:disable-next-line:max-line-length
                xLessonWebservice
                    .update(
                        this.lesson,
                        this.subProcess,
                        this.lesson.mandatory,
                        this.selectedTags,
                        this.selectedProjectDropdown ? this.selectedProjectDropdown.id : 0,
                        this.parentLesson
                    )
                    .then(() => {
                        this.$buefy.toast.open({
                            duration: 3000,
                            message: this.$t('toastMessages.update.lesson').toString(),
                            type: 'is-success',
                        });
                        this.toggleModal(this.subProcess);
                        this.$emit('syncKnowledge');
                        this.disabled = false;
                    })
                    .catch((error) => {
                        this.disabled = false;
                        console.log(error);
                    });
            }
        }
    }

    private toggleConfirmModal(answer) {
        this.isConfirmModalActive = !this.isConfirmModalActive;
        if (answer === true) {
            this.archive();
        }
    }

    private toggleModal(subProcess: XSubProcess | null = null) {
        this.$emit('toggleStoreUpdateLessonModal', this.selectedPhase, subProcess);
    }

    private archive() {
        xLessonWebservice
            .remove(this.lesson)
            .then(() => {
                this.$buefy.toast.open({
                    duration: 3000,
                    message: this.$t('toastMessages.delete.lesson').toString(),
                    type: 'is-success',
                });
                this.toggleModal(this.subProcess);
                this.$emit('syncKnowledge');
            })
            .catch((error) => {
                console.log(error);
            });
    }

    private validate() {
        if (!this.lesson.insight || this.lesson.insight === '') {
            this.errorMsg = 'insight';
            return 0;
        } else if (this.subProcess === null || !this.subProcess.title || this.subProcess.title === '') {
            this.errorMsg = 'subprocess';
            return 0;
        } else {
            return true;
        }
    }
}
